import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import {
	ICreateOwnerTypeRequestData,
	IGetCameraStatusRequestData,
	IGetCameraStatusResponseData,
	ICreateVisitTypeRequestData,
	IGetCameraStreamStatusResponse,
	IGetCamerasListRequestData,
	IGetCamerasListResponseData,
	IGetDeviceStatusResponseData,
	IGetOwnerTypeResponseData,
	IGetSiteConfigResponseData,
	IGetSocietyConfigResponseData,
	IGetStreamUrlAPIResponse,
	IGetSystemStatusResponseData,
	IGetVisitTypesResponseData,
	IPartnerDetails,
	IRegisteredFlatsList,
	IUpdateSiteConfigRequestData,
	IVehicleBrandDetails,
	IVehicleCategoryDetails,
	IVehicleColorDetails,
	IClientDetails,
	INodeDetails,
	ICreateClientRequestData,
	ICreateNodeRequestData,
	IEditClientRequestData,
	IEditNodeRequestData,
	IForceSyncResponseData,
	IGetForceSyncStatusResponseData
} from "../../interfaces";
import { queries } from "./queries.reducer";

const settings = queries.injectEndpoints({
	endpoints: (builder) => ({
		// GET SITE CONFIG
		getSiteConfig: builder.query<IGetSiteConfigResponseData, void | null>({
			query: () => ({
				url: "/settings/get_config",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET VISIT TYPES
		getVisitTypes: builder.query<IGetVisitTypesResponseData, void>({
			query: () => ({
				url: "/analytics/visittypes",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET SOCIETY CONFIG
		getSocietyConfig: builder.query<IGetSocietyConfigResponseData, void | null>({
			query: () => ({
				url: "/analytics/society",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET OWNER TYPES
		getOwnerTypes: builder.query<IGetOwnerTypeResponseData, void>({
			query: () => ({
				url: "/analytics/ownertypes",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET DEVICE STATUS
		getDeviceStatus: builder.query<IGetDeviceStatusResponseData, null>({
			query: () => ({
				url: "/settings/device_status",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET SYSTEM STATUS
		getSystemStatus: builder.query<IGetSystemStatusResponseData, null>({
			query: () => ({
				url: "/settings/docker_status",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET CAMERAS LIST
		getCamerasList: builder.query<IGetCamerasListResponseData, IGetCamerasListRequestData>({
			query: (requestData) => ({
				url: "/cameraconfig/camera",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// CREATE OWNER TYPE
		createOwnerType: builder.mutation<unknown, ICreateOwnerTypeRequestData>({
			query: (requestData) => ({
				url: "/analytics/ownertypes",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: (response) => {
				let errorMessage = "Something went wrong";

				const responseData = response.data as { [key in string]: [string] };

				if (responseData) {
					const [key, [message]] = Object.entries(responseData)[0];
					if (key === "type_of") {
						if (message === "owner type with this type of already exists.") {
							errorMessage = "continue";
						} else {
							errorMessage = message;
						}
					}
				}

				response.data = errorMessage;
				return response;
			}
		}),

		// GET REGISTERED FLATS LIST
		getRegisteredFlats: builder.query<IRegisteredFlatsList, void>({
			query: () => ({
				url: "/analytics/registered_flats",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// UPDATE SITE CONFIG
		updateSiteConfig: builder.mutation<unknown, IUpdateSiteConfigRequestData>({
			query: (requestData) => ({
				url: `/settings/config/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET CAMERA STREAM URL
		getCameraStreamUrl: builder.query<IGetStreamUrlAPIResponse, number>({
			query: (cameraId) => ({
				url: `/cameraconfig/camera/${cameraId}/get_stream_url`,
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET CAMERA STREAM STATUS
		getCameraStreamStatus: builder.query<IGetCameraStreamStatusResponse, number>({
			query: (cameraId) => ({
				url: `/cameraconfig/camera/${cameraId}/get_stream_status`,
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET PARTNER DETAILS
		getPartnerDetails: builder.query<IPartnerDetails, void | null>({
			query: () => ({
				url: "/analytics/download_logo",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// CREATE VISIT TYPE
		createVisitType: builder.query<unknown, ICreateVisitTypeRequestData>({
			query: (requestData) => ({
				url: "/analytics/visittypes",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			})
		}),

		// GET CAMERAS STATUS
		getCameraStatus: builder.query<IGetCameraStatusResponseData, IGetCameraStatusRequestData>({
			query: (requestData) => ({
				url: "/cameraconfig/camera/get_rtsp_status",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			keepUnusedDataFor: 0
		}),

		// GET VEHICLE BRANDS
		getVehicleBrands: builder.query<IVehicleBrandDetails[], void>({
			query: () => ({
				url: "/recon/analytics/vehiclebrand",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET VEHICLE CATEGORY
		getVehicleCategory: builder.query<IVehicleCategoryDetails[], void>({
			query: () => ({
				url: "/analytics/vehiclecategory",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET VEHICLE COLORS
		getVehicleColors: builder.query<IVehicleColorDetails[], void>({
			query: () => ({
				url: "/recon/analytics/vehiclecolor",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET CLIENTS LIST
		getClientsList: builder.query<IClientDetails[], void | null>({
			query: () => ({
				url: "/recon/analytics/client",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// GET NODES LIST
		getNodesList: builder.query<INodeDetails[], void | null>({
			query: () => ({
				url: "/recon/analytics/node",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// CREATE CLIENT
		createClient: builder.mutation<IClientDetails, ICreateClientRequestData>({
			query: (requestData) => ({
				url: "/recon/analytics/client",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// CREATE NODE
		createNode: builder.mutation<INodeDetails, ICreateNodeRequestData>({
			query: (requestData) => ({
				url: "/recon/analytics/node",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// EDIT CLIENT
		editClient: builder.mutation<IClientDetails, IEditClientRequestData>({
			query: (requestData) => ({
				url: `/recon/analytics/client/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// EDIT NODE
		editNode: builder.mutation<INodeDetails, IEditNodeRequestData>({
			query: (requestData) => ({
				url: `/recon/analytics/node/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// CHECK OTS STATUS
		checkOtsStatus: builder.query<IGetSiteConfigResponseData, void | null>({
			query: () => ({
				url: "/settings/ots_check",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// FORCE SYNC
		forceSync: builder.mutation<IForceSyncResponseData, void>({
			query: () => ({
				url: "/sync/force_sync",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET FORCE SYNC STATUS
		getForceSyncStatus: builder.query<IGetForceSyncStatusResponseData, null | void>({
			query: () => ({
				url: "/sync/force_sync_status",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		})
	})
});

export const {
	// GET SITE CONFIG
	useGetSiteConfigQuery,
	// GET VISIT TYPES
	useGetVisitTypesQuery,
	// GET SOCIETY CONFIG
	useGetSocietyConfigQuery,
	// GET SOCIETY CONFIG LAZY
	useLazyGetSocietyConfigQuery,
	// GET OWNER TYPES
	useGetOwnerTypesQuery,
	// GET OWNER TYPES LAZY
	useLazyGetOwnerTypesQuery,
	// GET DEVICE STATUS
	useGetDeviceStatusQuery,
	// GET SYSTEM STATUS
	useGetSystemStatusQuery,
	// GET CAMERAS LIST
	useGetCamerasListQuery,
	// GET CAMERAS LIST LAZY
	useLazyGetCamerasListQuery,
	// CREATE OWNER TYPE
	useCreateOwnerTypeMutation,
	// GET REGISTERED FLATS LIST
	useGetRegisteredFlatsQuery,
	// UPDATE SITE CONFIG
	useUpdateSiteConfigMutation,
	// GET CAMERA STREAM URL
	useGetCameraStreamUrlQuery,
	// GET CAMERA STREAM STATUS
	useLazyGetCameraStreamStatusQuery,
	// GET PARTNER DETAILS
	useGetPartnerDetailsQuery,
	// CREATE VISIT TYPE
	useLazyCreateVisitTypeQuery,
	// GET CAMERAS STATUS
	useGetCameraStatusQuery,
	// GET CAMERAS STATUS LAZY
	useLazyGetCameraStatusQuery,
	// GET VEHICLE BRANDS
	useGetVehicleBrandsQuery,
	// GET VEHICLE CATEGORY
	useGetVehicleCategoryQuery,
	// GET VEHICLE COLORS
	useGetVehicleColorsQuery,
	// GET CLIENTS LIST
	useGetClientsListQuery,
	// GET NODES LIST
	useGetNodesListQuery,
	// CREATE CLIENT
	useCreateClientMutation,
	// CREATE NODE
	useCreateNodeMutation,
	// EDIT CLIENT
	useEditClientMutation,
	// EDIT NODE
	useEditNodeMutation,
	// CHECK OTS STATUS
	useCheckOtsStatusQuery,
	// FORCE SYNC
	useForceSyncMutation,
	// GET FORCE SYNC STATUS
	useGetForceSyncStatusQuery
} = settings;

export default settings;
